import { Modal } from '@octano/global-ui';
import { useTranslation } from 'react-i18next';
import { Container, Row, Col } from 'reactstrap';
import {
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
  Ref,
} from 'react';
import { TranslationsKeys } from '../../../../locales/translations';
import { useInstitutionsFormLoader } from './Loaders/InstitutionsFormLoader';
import InstitutionsForm from './InstitutionsForm';
import {
  Institution,
  InstitutionFields,
  InstitutionRequest,
} from '../interfaces/type';

export type InstitutionsModalMethods = {
  open: (row?: Institution) => void;
  close: () => void;
};

type InstitutionsModalProps = {
  create: (values: InstitutionRequest) => Promise<void> | void;
  update: (
    institutionId: string | number,
    values: InstitutionRequest,
  ) => Promise<void> | void;
};

function InstitutionsModal(
  { create, update }: InstitutionsModalProps,
  ref: Ref<InstitutionsModalMethods>,
) {
  const { t } = useTranslation(TranslationsKeys.CONFIGURATION_INSTITUTIONS);
  const { data } = useInstitutionsFormLoader();

  const [saving, setSaving] = useState(false);
  const [visible, setVisible] = useState(false);
  const [institution, setInstitution] = useState<Institution>();

  const close = useCallback(() => {
    setInstitution(undefined);
    setVisible(false);
  }, []);

  const open = useCallback((row?: Institution) => {
    setInstitution(row);
    setVisible(true);
  }, []);

  useImperativeHandle(ref, () => ({ open, close }));

  const handleSave = useCallback(
    async (values: InstitutionFields) => {
      if (saving) {
        return;
      }
      setSaving(true);
      if (institution?.id) {
        await update(institution?.id, {
          name: values?.name,
          code: values?.code,
          countryId: values?.country?.value?.toString(),
        });
      } else {
        await create({
          name: values?.name,
          code: values?.code,
          countryId: values?.country?.value?.toString(),
        });
      }
      setSaving(false);
    },
    [saving, institution, create, update],
  );

  return (
    <>
      {!!visible && (
        <Modal isOpen={true} toggle={close}>
          <Container>
            <Row>
              <Col xs={12}>
                <h1 className="text-dark text-center fw-600 fs-20">
                  {institution?.id
                    ? t(`modal.update.title`)
                    : t(`modal.create.title`)}
                </h1>
                <p className=" text-center fs-16 mb-4">
                  {institution?.id
                    ? t(`modal.update.description`)
                    : t(`modal.create.description`)}
                </p>
              </Col>
            </Row>
            <InstitutionsForm
              {...data}
              saving={saving}
              onCancel={close}
              onSubmit={handleSave}
              defaultValues={{
                code: institution?.code || '',
                name: institution?.name || '',
                country: institution?.country
                  ? {
                      label: institution?.country?.name,
                      value: institution?.country?.id,
                    }
                  : undefined,
              }}
            />
          </Container>
        </Modal>
      )}
    </>
  );
}

export default forwardRef(InstitutionsModal);
